<template>
    <div class="cancel-container">
        <h1 class="header-1">{{ $t('cancellation.title') }}</h1>
        <p class="description">
            {{ $t('cancellation.description') }}
        </p>

        <div class="reasons">
            <label v-for="reason in reasons" :key="reason.value" class="radio-option">
                <input type="radio" :value="reason.value" v-model="selectedReason" />
                <span class="custom-radio"></span>
                {{ reason.label }}
            </label>

            <textarea v-if="selectedReason === 'Other'" v-model="otherReason"
                :placeholder="$t('cancellation.otherPlaceholder')" class="other-input">
            </textarea>
        </div>

        <div class="actions">
            <PrimaryBtn class="switch-plan-btn" @click="handleSwitchPlan">
                {{ $t('cancellation.switchPlan') }}
            </PrimaryBtn>
            <button class="cancel-anyway-btn" :disabled="!isFormValid" @click="handleCancel">
                {{ $t('cancellation.cancelAnyway') }}
            </button>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import PrimaryBtn from "@/components/BaseComponents/PrimaryBtn.vue";
import { subscriptionAPI } from "@/services/api";
import { trackEvent } from "@/utils/analytics";
import { useI18n } from 'vue-i18n';

const router = useRouter();
const store = useStore();
const { t } = useI18n();

const reasons = computed(() => [
    { value: 'No longer needed', label: t('cancellation.reasons.noLongerNeeded') },
    { value: 'Too expensive', label: t('cancellation.reasons.tooExpensive') },
    { value: 'Not satisfied with the service', label: t('cancellation.reasons.notSatisfied') },
    { value: 'Switching to another provider', label: t('cancellation.reasons.switching') },
    { value: 'Other', label: t('cancellation.reasons.other') },
]);

const selectedReason = ref("");
const otherReason = ref("");

const isFormValid = computed(() => {
    return selectedReason.value && (selectedReason.value !== "Other" || otherReason.value.trim() !== "");
});

const handleSwitchPlan = () => {
    trackEvent("cancel_flow_abandoned", {
        action: "switch_plan_clicked",
        current_plan: store.state.activePlan,
        selected_reason: selectedReason.value || "none",
    });

    router.push("/plans");
};

const handleCancel = async () => {
    if (!isFormValid.value) return;

    const finalReason =
        selectedReason.value === "Other" ? otherReason.value : selectedReason.value;

    trackEvent("subscription_cancellation_started", {
        current_plan: store.state.activePlan,
        cancellation_reason: finalReason,
    });

    try {
        const sessionId = store.state.sessionId;
        const subscriptionId = store.state.userDetails?.subscription_id;

        if (!subscriptionId) {
            throw new Error('No subscription ID found');
        }

        await subscriptionAPI.cancelSubscription(
            subscriptionId,
            encodeURIComponent(finalReason),
            sessionId
        );

        trackEvent("subscription_cancelled", {
            cancelled_plan: store.state.activePlan,
            cancellation_reason: finalReason,
        });

        // Refresh user details after cancellation
        await store.dispatch('fetchUserDetails');
        
        router.push("/subcancelsuccess");
    } catch (error) {
        console.error("Error canceling subscription:", error);
        trackEvent("subscription_cancellation_failed", {
            current_plan: store.state.activePlan,
            cancellation_reason: finalReason,
        });
    }
};
</script>

<style scoped>
.header-1 {
    margin-top: 0;
}

.cancel-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    background: var(--bg-card);
    width: 80%;
    max-width: 600px;
    padding: var(--spacing-4xl);
    border-radius: var(--border-radius-xl);
    box-shadow: var(--shadow-card);
    text-align: center;
}

.description {
    color: var(--color-text-secondary);
    margin: 0 0 var(--spacing-notification);
}

.reasons {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 85%;
    gap: var(--gap-lg);
}

.radio-option {
    display: flex;
    align-items: center;
    cursor: pointer;
    font-size: var(--font-size-body);
    color: var(--color-white);
    position: relative;
}

.radio-option input {
    display: none;
}

.custom-radio {
    width: 20px;
    height: 20px;
    border-radius: var(--border-radius-circle);
    background: var(--color-white-20);
    margin-right: var(--gap-lg);
    transition: var(--transition-normal);
    position: relative;
}

.radio-option input:checked+.custom-radio {
    background: var(--gradient-success);
}

.other-input {
    width: 100%;
    padding: var(--padding-md);
    border-radius: var(--border-radius-md);
    border: none;
    background: var(--color-white-20);
    color: var(--color-white);
    font-size: var(--font-size-caption);
    outline: none;
    resize: none;
    min-height: 60px;
}

.actions {
    margin-top: var(--spacing-3xl);
    display: flex;
    flex-direction: column;
    align-items: center;
}

.switch-plan-btn {
    width: 100%;
    max-width: 300px;
    margin-bottom: var(--margin-md);
}

.cancel-anyway-btn {
    width: 100%;
    max-width: 300px;
    padding: var(--padding-md);
    border: none;
    border-radius: var(--border-radius-md);
    background: var(--color-white-20);
    color: var(--color-white);
    cursor: pointer;
    transition: var(--transition-normal);
    text-align: center;
}

.cancel-anyway-btn:disabled {
    opacity: var(--opacity-disabled);
    cursor: not-allowed;
}

.cancel-anyway-btn:hover:not(:disabled) {
    background: var(--color-white-40);
}
</style>
